import { EventBus, GameEvents } from '../core/EventBus';
import { GameConfig } from '../core/GameConfig';
import { Utils } from '../core/Utils';
import { GameData } from '../data/GameData';
import { PlayerState, SaveData, AttributeKey, AuditionQualified, OwnedParts } from '../data/types';
import { normalNpcSystem } from './NormalNpcSystem';
import { randomPlayerName, randomPlayerNameParts } from '../data/tables/NamePool';
import { getScheduledScripts, poolEntryAbsOf, POOL_MONTHS } from '../data/tables/ScheduleTable';
import { specialDeadlineAbs } from '../data/tables/SpecialScripts';
import { castingNightDue } from './ScriptSystem';
import { TAG_EFFECTS } from './TagEffects';
import { noticeSystem } from './NoticeSystem';
import { prefetchAvatar } from '../avatar/gamemount';
import { ASSISTANT_AVATAR } from '../data/Assistant';
import { ORIGIN_ORDINARY, LEGACY_ORIGIN_TAGS } from '../data/tables/Tags';
import { STARTER_GIFTS } from '../data/tables/Gifts';
import { AvatarConfig, DEFAULT_CONFIG, applyFrontSideRule } from '../avatar/config';

/** 可购买的头像部件维度（五类店共用清单；与 OwnedParts 对齐，⛔ 与 AvatarConfig 漂移） */
const OWNED_DIMS = [
    'face', 'skin', 'hairFront', 'hairSides', 'hairBack', 'hairColor',
    'brow', 'eye', 'eyeColor', 'nose', 'mouth', 'cloth', 'glasses', 'acc',
] as const;
type OwnedDim = typeof OWNED_DIMS[number];
/** OWNED_DIMS 的查表形态：区分「商品槽」与「随脸走的非商品槽」（性别/微调/胡须/岁月痕迹） */
const OWNED_SET = new Set<string>(OWNED_DIMS as readonly string[]);

/**
 * 游戏状态管理器（单例）
 * 持有玩家全部运行时数据，是所有系统的数据中枢
 */
export class GameState {
    private static instance: GameState;

    /** 玩家状态 */
    player: PlayerState;
    /** 当前阶段 */
    currentStage: number;
    /** 阶段起始年龄 */
    stageStartAge: number;
    /** 存档版本号 */
    version: number;

    private constructor() { }

    static getInstance(): GameState {
        if (!this.instance) {
            this.instance = new GameState();
        }
        return this.instance;
    }

    /** 重置单例（用于测试/周目重置） */
    static reset(): void {
        this.instance = null as unknown as GameState;
    }

    /** 创建全新 PlayerState（开局默认值） */
    private static createDefaultPlayer(): PlayerState {
        const defs = GameData.ATTRIBUTE_DEFS;
        const nameParts = randomPlayerNameParts('female');
        return {
            name: nameParts.surname + nameParts.given,
            surname: nameParts.surname,
            givenName: nameParts.given,
            nickname: '',
            age: GameConfig.START_AGE,
            year: 0,
            month: 1,
            week: 1,
            gender: 'female',
            attributes: {
                charm: Utils.randomInt(defs.charm.startRange[0], defs.charm.startRange[1]),
                talent: Utils.randomInt(defs.talent.startRange[0], defs.talent.startRange[1]),
                temperament: Utils.randomInt(defs.temperament.startRange[0], defs.temperament.startRange[1]),
                stamina: Utils.randomInt(defs.stamina.startRange[0], defs.stamina.startRange[1]),
                eq: Utils.randomInt(defs.eq.startRange[0], defs.eq.startRange[1]),
                wisdom: Utils.randomInt(defs.wisdom.startRange[0], defs.wisdom.startRange[1]),
                artistry: Utils.randomInt(defs.artistry.startRange[0], defs.artistry.startRange[1]),
                reputation: Utils.randomInt(defs.reputation.startRange[0], defs.reputation.startRange[1]),
            },
            resources: {
                energy: GameConfig.START_ENERGY,
                money: GameConfig.START_MONEY,
            },
            fame: 0,
            rank: 1,
            // 剧宣批 v11：粉丝落盘=名气×K×个体号召力（开局咖位购买经 setRank 抬 fame 后会在 setRank 内同步）
            fans: 0,
            fanCharisma: GameState.rollFanCharisma(),
            pathCourseCounts: { charm: 0, talent: 0, temperament: 0, stamina: 0, eq: 0, wisdom: 0, artistry: 0, reputation: 0 },
            // 开局身份（2026-10-19 拆分）：新档**不预选**（用户令「不要默认选中素人」），
            // 由建角页 player.origin 由 setOrigin 写入；身份不进 tags、不占天赋槽。
            // 缺省留空：不要在此补 ORIGIN_ORDINARY（老档反推在 migratePlayer 里）
            originTrainingMonth: 0,
            tags: [],
            flags: {},
            contracts: [],
            normalNpcs: [],
            relationships: {},
            schedule: [null, null, null, null],
            socialCounts: { chat: 0, invite: 0 },
            // 背包（熟遇改版 2026-10-10 新建）：开局发一批测试礼物，⛔ 花钱买
            items: GameState.starterItems(),
            lastPostMonth: null,
            postRecords: [],
            marketScripts: [],
            auditionQualified: [],
            scriptShown: [],
            marketMode: 'scheduled',
            managerNotices: [],
            lastReadNoticeCount: 0,
            greetings: [],
            assistantMessages: [],
            assistantReadCount: 0,
            totalEarnedMoney: 0,
            totalShotsCompleted: 0,
            // 本周目解锁的成就（结局批 2026-09-30：结算页「周目结算」节按此全量展示；
            // checkMonthly 解锁时同步 push。旧档缺省 undefined → 终局兜底退化为补判那一次，⛔ 不升版）
            runAchievements: [],
            filmography: [],
            // 年度奖项（批A）：三个字段均「缺省即空」，旧档缺字段无需迁移（读取端 ??= / ?? [] 兜底）
            awardHistory: [],
            pendingAward: null,
            ceremonySeen: [],
        };
    }

    /** 开局背包（熟遇改版 2026-10-10：礼物来源系统未做，新档与老档都从这一份补充起步） */
    static starterItems(): Record<string, number> {
        const bag: Record<string, number> = {};
        STARTER_GIFTS.forEach(g => { bag[g.id] = g.count; });
        return bag;
    }

    /**
     * 开局部件所有权（自由出行批 2026-10-11）：每个可购维度以「当前穿着的那件」为初始拥有
     * （建角初始装备=白T 等）。cfg 缺省读 DEFAULT_CONFIG（开局确认前占位）。
     */
    static starterOwned(cfg?: AvatarConfig): OwnedParts {
        const src = cfg ?? DEFAULT_CONFIG;
        const owned = {} as OwnedParts;
        OWNED_DIMS.forEach(d => { owned[d] = [src[d]]; });
        return owned;
    }

    /**
     * 所有权兜底（**只准 `migratePlayer` 与建角确认调用**，2026-10-12 C 批收口）：owned 缺失时按 cfg
     * （缺省 player.avatar ?? DEFAULT_CONFIG）补初始件；已有 owned 时把当前穿着并入。
     * ⛔ 商店/造型间等运行时 UI 再调它——「追认 avatar 现状」这条通道会把预览中的未买件洗白成永久拥有
     * （实测 0 元到手，见《自由出行与小地图-设计方案.md》§十一 结构待办 8）。UI 侧落档走 `wearDraft`。
     */
    ensureOwned(cfg?: AvatarConfig): void {
        const p = this.player;
        if (!p.owned) p.owned = GameState.starterOwned(cfg ?? p.avatar ?? undefined);
        const src = cfg ?? p.avatar;
        if (!src) return;
        OWNED_DIMS.forEach(d => {
            const arr = p.owned![d] ?? (p.owned![d] = []);
            const v = src[d];
            if (!arr.includes(v)) arr.push(v);
        });
    }

    /** 这件部件是否已拥有（商店价签/置灰与造型间过滤共用同一判据） */
    ownsPart(dim: OwnedDim, index: number): boolean {
        return (this.player.owned?.[dim] as number[] | undefined)?.includes(index) ?? false;
    }

    /**
     * 把一份草稿脸落成穿着（部件店与造型间共用，2026-10-12 C 批）：逐槽问 `ownsPart`，
     * 没买的那件回退现值；回退后再跑一次 `applyFrontSideRule` 保证刘海/侧发/后发组合合法——
     * 被规则强制顶出来的那一槽不是玩家挑的商品（不放行就穿不上），随本次落档并入 owned。
     * 非商品槽（性别、鼻/嘴微调、胡须、岁月痕迹）一律随草稿走。
     * ⛔ 再拿整张草稿直写 `player.avatar`：旧写法把预览过的未买件一起穿上，再经 `ensureOwned` 洗白。
     */
    wearDraft(draft: AvatarConfig): void {
        const p = this.player;
        if (!p.owned) p.owned = GameState.starterOwned(p.avatar ?? undefined);
        const merged = { ...(p.avatar ?? DEFAULT_CONFIG) } as AvatarConfig;
        const slots = merged as unknown as Record<string, number>;
        (Object.keys(draft) as (keyof AvatarConfig)[]).forEach(k => {
            if (!OWNED_SET.has(k as string)) slots[k as string] = draft[k] as number;
        });
        OWNED_DIMS.forEach(d => {
            const v = draft[d];
            if (this.ownsPart(d, v)) slots[d as string] = v;
        });
        const after = applyFrontSideRule(merged);
        OWNED_DIMS.forEach(d => {
            if (after[d] === slots[d as string]) return;
            const arr = p.owned![d] ?? (p.owned![d] = []);
            if (!arr.includes(after[d])) arr.push(after[d]);
        });
        p.avatar = after;
    }

    /**
     * 部件购买（五类店共用，价签购买流）：付款 → 加入 owned + 立即上身（写 player.avatar）。
     * 已拥有 = 免费（回店换款走 ensure/apply 路径，不进这里）。
     */
    buyPart(dim: OwnedDim, index: number, price: number): { ok: boolean; reason?: string } {
        const p = this.player;
        const arr = p.owned?.[dim] ?? [];
        if (arr.includes(index)) return { ok: false, reason: '已拥有' };
        if (this.getMoney() < price) return { ok: false, reason: '金钱不足' };
        this.addMoney(-price);
        (p.owned![dim] ??= []).push(index);
        p.avatar = { ...(p.avatar ?? DEFAULT_CONFIG), [dim]: index } as AvatarConfig;
        return { ok: true };
    }

    /** 初始化 NPC 好感度 */
    private initRelationships(): void {
        const rel: Record<string, number> = {};
        GameData.NPCS.forEach(npc => {
            rel[npc.id] = npc.initialFavor;
        });
        this.player.relationships = rel;
    }

    /** 重新随机开局六项属性（开局页 [🔄 刷新] 用） */
    rerollStartAttributes(): void {
        const defs = GameData.ATTRIBUTE_DEFS;
        (Object.keys(defs) as AttributeKey[]).forEach(key => {
            this.setAttribute(key, Utils.randomInt(defs[key].startRange[0], defs[key].startRange[1]));
        });
    }

    /** 开始新游戏 */
    createNewGame(): void {
        this.player = GameState.createDefaultPlayer();
        this.currentStage = 1;
        this.stageStartAge = GameConfig.START_AGE;
        this.version = GameConfig.SAVE_VERSION;
        this.initRelationships();
        // 开局生成一批普通 NPC（跨季度常驻，咖位随接戏成长）
        normalNpcSystem.generateBatch(GameConfig.NORMAL_NPC_COUNT);
        // 竞争型特殊 NPC 一并实例化进同一派角池（锚点/剧情用；身份从 NPCDef 派生）
        normalNpcSystem.generateSpecialCompetitors();
        // 开局刷一批剧本
        this.refreshMarket();
        this.warmAvatarCache();
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /**
     * 捏脸出图缓存预热：主角 + 全部普通 NPC + 配了定稿脸的固定 NPC（NPCDef.avatar）按 master 尺寸渲好存进缓存。
     * fire-and-forget（⛔ 别 await，进场动画不等画师），切页时头像直接命中缓存，不再闪 emoji 兜底。
     */
    warmAvatarCache(): void {
        prefetchAvatar(this.player?.avatar);
        (this.player?.normalNpcs ?? []).forEach(npc => prefetchAvatar(npc.avatar));
        // 固定 NPC 的定稿脸（NPCDef.avatar，许林辉首个）：演出面（偶遇/剧情/颁奖夜）也按 master 尺寸要这张，
        // 不预热的话第一次上屏会空一帧才换脸（2026-10-13 接演出面时补）
        GameData.NPCS.forEach(npc => prefetchAvatar(npc.avatar));
        prefetchAvatar(ASSISTANT_AVATAR); // 小助理（固定 config，见 data/Assistant）
    }

    /** 从存档恢复 */
    load(save: SaveData): void {
        this.player = save.player;
        this.currentStage = save.currentStage;
        this.stageStartAge = save.stageStartAge;
        this.version = save.version;
        // 老版本存档字段迁移
        this.migratePlayer();
        // 竞争型特殊 NPC 补入派角池（旧档在本能力上线前不含她们，幂等迁移）——2026-10-15
        normalNpcSystem.generateSpecialCompetitors();
        // 存档中剧本池为空（旧档 / 修复前空市场残留）时，按当前咖位重新刷一批，避免试镜周无剧本可点
        if (!Array.isArray(this.player.marketScripts) || this.player.marketScripts.length === 0) {
            this.refreshMarket();
        }
        // 月存口径（2026-11-03 茄子令「一个月只存一次」）：合法档恒是「月初那一格」——`advanceMonth`
        // 已把 week 归 1、排期清空，所以这里是空操作。留着它只为一件事：**旧的周界档**（10-28~11-02 那批
        // 存在第 2~4 周上的档）读回来不许带着一份「本月已排好」的假现场进决策日，一律归位到月初重排。
        // 老档折一次就没了（下一次写盘就是新格），不许把它当常规清理用。
        this.player.schedule = [null, null, null, null];
        this.player.week = 1;
        this.warmAvatarCache();
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /** 老版本存档字段迁移（V1→V2） */
    private migratePlayer(): void {
        const p = this.player;
        if (!p) return;

        // V2 新增字段
        // name: 旧档无此字段 → 随机一个默认姓名
        if (!p.name) p.name = randomPlayerName(p.gender ?? 'female');
        // 昵称（V4 新增）：旧档缺省 = 空（小助理用“姐”兜底称呼）
        if (typeof p.nickname !== 'string') p.nickname = '';
        // 姓氏 / 名字（2026-10-19 拆分）：旧档按「单字姓 + 其余为名」回填，`name` 仍是全名（展示侧只读它）
        if (typeof p.surname !== 'string') p.surname = '';
        if (typeof p.givenName !== 'string') p.givenName = '';
        if (!p.surname || !p.givenName) {
            p.surname = p.name.slice(0, 1);
            p.givenName = p.name.slice(1);
        }
        // gender: 旧档无此字段 → 默认 'female'
        if (!p.gender) (p as unknown as Record<string, unknown>).gender = 'female';
        // brandRejected 旧口径折算（2026-10-30 5:5 批改 5 年拉黑窗口）：旧 string[]（永久拉黑）折成
        // { id, abs: 读档时点 }——从读档起再拉黑 5 年；新档/已折档跳过
        const legacyRejected = p.brandRejected as unknown;
        if (Array.isArray(legacyRejected) && legacyRejected.length > 0 && typeof legacyRejected[0] === 'string') {
            const now = p.year * 12 + p.month;
            p.brandRejected = (legacyRejected as string[]).map(id => ({ id, abs: now }));
        }
        // inviteMsgs → chatMsgs（2026-10-28 聊天回放账统一；inviteMsgs 只存活了数小时，折进新账即删）
        const legacyInvites = (p as unknown as Record<string, unknown>).inviteMsgs as { npcId: string; text: string; abs: number }[] | undefined;
        if (Array.isArray(legacyInvites) && legacyInvites.length) {
            p.chatMsgs = [...legacyInvites.map(m => ({ npcId: m.npcId, me: false, text: m.text, abs: m.abs })), ...(p.chatMsgs ?? [])];
            delete (p as unknown as Record<string, unknown>).inviteMsgs;
        }
        // normalNpcs: 旧档无 → []
        if (!Array.isArray((p as unknown as Record<string, unknown>).normalNpcs)) (p as unknown as Record<string, unknown>).normalNpcs = [];

        // pathCourseCounts: 训练路径累计次数（V5 新增）：旧档补全八键 0
        if (!p.pathCourseCounts || typeof p.pathCourseCounts !== 'object') {
            p.pathCourseCounts = { charm: 0, talent: 0, temperament: 0, stamina: 0, eq: 0, wisdom: 0, artistry: 0, reputation: 0 };
        } else {
            (['charm', 'talent', 'temperament', 'stamina', 'eq', 'wisdom', 'artistry', 'reputation'] as AttributeKey[]).forEach(k => {
                if (typeof p.pathCourseCounts[k] !== 'number') p.pathCourseCounts[k] = 0;
            });
        }

        // attributes: 智慧/才艺（属性扩展新增）：旧档缺 key 统一补 10
        if (typeof p.attributes?.wisdom !== 'number') p.attributes.wisdom = 10;
        if (typeof p.attributes?.artistry !== 'number') p.attributes.artistry = 10;

        // auditionQualified: 兼容链 V0 Record → V1 string[] → V2 AuditionQualified[]（{scriptId, roleIndex}）
        const oldQualified = p.auditionQualified as unknown;
        if (oldQualified !== undefined && !Array.isArray(oldQualified)) {
            // V0: Record<剧本ID, 是否成功> → string[]（保留成功项）
            p.auditionQualified = Object.keys(oldQualified as Record<string, unknown>)
                .filter(k => (oldQualified as Record<string, boolean>)[k])
                .map(id => ({ scriptId: id, roleIndex: 0 }));
        } else if (Array.isArray(p.auditionQualified) && p.auditionQualified.length > 0
            && typeof p.auditionQualified[0] === 'string') {
            // V1: string[] → 映射 roleIndex 0（V1 每本仅一个角色）
            p.auditionQualified = (p.auditionQualified as unknown as string[]).map(id => ({
                scriptId: id,
                roleIndex: 0,
            }));
        }
        if (!Array.isArray(p.auditionQualified)) p.auditionQualified = [];

        // Contract: V1 无 roleIndex → 默认 0
        if (Array.isArray(p.contracts)) {
            p.contracts.forEach(c => {
                if ((c as unknown as Record<string, unknown>).roleIndex === undefined) (c as unknown as Record<string, unknown>).roleIndex = 0;
            });
        }

        // pendingScripts → contracts（旧 model 无剩余周数/状态信息，直接作废清空）
        const legacy = p as Partial<PlayerState> & { pendingScripts?: unknown };
        if (legacy.pendingScripts !== undefined) {
            p.contracts = [];
            delete legacy.pendingScripts;
        }
        if (!Array.isArray(p.contracts)) p.contracts = [];

        // hasPosted → lastPostMonth / postRecords（只保留最近一次发布月份）
        if ((p as { hasPosted?: boolean }).hasPosted !== undefined) {
            p.lastPostMonth = (p as { hasPosted?: boolean }).hasPosted
                ? p.year * 12 + p.month
                : null;
            p.postRecords = p.lastPostMonth !== null
                ? [{ month: p.lastPostMonth, type: '营业', text: '', likes: 0, reposts: 0 }]
                : [];
            delete (p as { hasPosted?: boolean }).hasPosted;
        }
        if (p.lastPostMonth === undefined) p.lastPostMonth = null;
        if (!Array.isArray(p.postRecords)) p.postRecords = [];
        // 剧宣批 v11：粉丝落盘字段（正常档由 SAVE_VERSION 拒载保证存在；此处仅防测试桩构造的半份 state）
        if (typeof p.fanCharisma !== 'number') p.fanCharisma = GameState.rollFanCharisma();
        if (typeof p.fans !== 'number') p.fans = Math.round(p.fame * GameConfig.FANS_ANCHOR_K * p.fanCharisma);
        // 背包（熟遇改版 2026-10-10 新增）：老档缺字段补开局测试礼物（同「缺失时载入补 roll」先例，⛔ 升版本）
        if (!p.items || typeof p.items !== 'object') p.items = GameState.starterItems();
        // 日历化批（2026-10-27）：合同拍摄进度改记在合同上（Contract.shotWeeksDone）。老档一律被
        // SAVE_VERSION 拒载（升版即作废，用户 2026-10-27 拍板「直接作废」），无需迁移在途合同。
        // 自由出行批（2026-10-11）：部件所有权 / 周末有约挂起 / 探班记账（缺省补齐，⛔ 升 SAVE_VERSION）
        if (!p.owned) p.owned = GameState.starterOwned(p.avatar ?? undefined);
        this.ensureOwned(); // 老档身上穿着的装备并入拥有（防「穿着的却没拥有」）
        // 多槽批（2026-10-29）：旧单槽 pendingInvite → pendingInvites 数组（折账后删旧字段，⛔ 升 SAVE_VERSION）
        //  折账只在这一处做（沿用 pendingScripts→contracts 先例）：旧档挂起保留、不丢
        const legacyInvite = (p as PlayerState & { pendingInvite?: { npcId?: string; week?: number } | null }).pendingInvite;
        if (!Array.isArray(p.pendingInvites)) p.pendingInvites = [];
        if (legacyInvite && legacyInvite.npcId
            && !p.pendingInvites.some(i => i.npcId === legacyInvite.npcId)) {
            p.pendingInvites.push({ npcId: legacyInvite.npcId, week: legacyInvite.week });
        }
        delete (p as PlayerState & { pendingInvite?: unknown }).pendingInvite;
        if (!p.visitMonth || typeof p.visitMonth !== 'object') p.visitMonth = {};
        // 旧格式 number[]（纯月份）→ PostRecord[]（兼容老存档）
        if (p.postRecords.length > 0 && typeof p.postRecords[0] === 'number') {
            p.postRecords = (p.postRecords as unknown as number[])
                .filter(m => m !== null)
                .map(m => ({ month: m as number, type: '营业', text: '', likes: 0, reposts: 0 }));
        }

        // 剧本展示去重记录（新字段）
        if (!Array.isArray(p.scriptShown)) p.scriptShown = [];
        // 剧本池模式（V3 新增）：旧档无此字段 → 默认固定排期模式
        if (!p.marketMode) p.marketMode = 'scheduled';
        // 经纪人通知历史（新字段）
        if (!Array.isArray(p.managerNotices)) p.managerNotices = [];
        // 上次已读通知条数（老档无此字段 → 按当前通知数计为已读，避免老档进游戏就有未读）
        if (typeof p.lastReadNoticeCount !== 'number') p.lastReadNoticeCount = p.managerNotices.length;
        // NPC 打招呼队列（批次 B 新增）：旧档缺字段补空
        if (!Array.isArray(p.greetings)) p.greetings = [];
        // 助理播报消息（V4 新增）
        if (!Array.isArray(p.assistantMessages)) p.assistantMessages = [];
        // 助理已读条数（V4 新增）：老档缺省 = 已有消息数（历史不补播），避免老档进游戏角标堆积
        if (typeof p.assistantReadCount !== 'number') p.assistantReadCount = p.assistantMessages.length;

        // 每周时段对象（旧版本可能是 string[]）
        if (Array.isArray(p.schedule)) {
            p.schedule = p.schedule.map(slot =>
                typeof slot === 'string'
                    ? { kind: 'course', refId: slot } as PlayerState['schedule'][number]
                    : slot
            );
        }

        // NPC 好感旧体系 → 未结识（-1）体系：旧档中已结识（原起始好感 ≥0）的 NPC 平移保留，缺失项补 -1
        if (p.relationships && typeof p.relationships === 'object') {
            for (const npc of GameData.NPCS) {
                const raw = p.relationships[npc.id];
                if (raw === undefined) {
                    // 旧档缺失该 NPC：继承新版初始值（V1 全部 -1）
                    p.relationships[npc.id] = npc.initialFavor;
                } else if (raw < 0) {
                    // 旧档无负值；有则一律归 -1 未结识
                    p.relationships[npc.id] = -1;
                } else {
                    // 已结识：平移保留原好感（0 起步）
                    p.relationships[npc.id] = Math.max(0, raw);
                }
            }
        } else {
            p.relationships = {};
            GameData.NPCS.forEach(npc => { p.relationships[npc.id] = npc.initialFavor; });
        }

        // 开局身份（2026-10-19 拆分）：旧档无 origin → 按老身份标签反推；老标签从 tags 摘除，
        // 此后身份只走 player.origin（不占天赋槽、不重复显示）。不升 SAVE_VERSION。
        if (!Array.isArray(p.tags)) p.tags = [];
        if (typeof p.origin !== 'string' || !p.origin) {
            p.origin = p.tags.includes('TAG_013') ? 'TAG_013'
                : p.tags.includes('TAG_012') ? 'TAG_012'
                    : p.tags.includes('TAG_001') ? 'TAG_001'
                        : ORIGIN_ORDINARY;
        }
        p.tags = p.tags.filter(id => !(LEGACY_ORIGIN_TAGS as readonly string[]).includes(id));
        if (typeof p.originTrainingMonth !== 'number') p.originTrainingMonth = 0;
    }

    /** 导出存档数据 */
    toSave(): SaveData {
        return {
            version: this.version,
            timestamp: Date.now(),
            player: this.player,
            currentStage: this.currentStage,
            stageStartAge: this.stageStartAge,
        };
    }

    // ===== 基础读写 API =====

    getAttribute(key: AttributeKey): number {
        return this.player.attributes[key];
    }

    setAttribute(key: AttributeKey, value: number): void {
        const def = GameData.ATTRIBUTE_DEFS[key];
        this.player.attributes[key] = Utils.clamp(value, def.min, def.max);
        EventBus.emit(GameEvents.ATTRIBUTE_CHANGED, key, this.player.attributes[key]);
    }

    addAttribute(key: AttributeKey, delta: number): void {
        // 黑料缠身（TAG_401）：**负向口碑** ×1.5（正向不乘）。落点选这里＝全库口碑增减的总闸，
        // 作品低分 / 事件失败分支 / 黑帖数值事件 一次覆盖，⛔ 各处再各写一遍。
        // 取整＝按绝对值四舍五入再带回符号（−3 × 1.5 = −4.5 ⇒ −5；⛔ JS `Math.round(-4.5)` 的 −4）。
        if (key === 'reputation' && delta < 0) {
            const m = this.traitMult('reputationLossMult');
            if (m !== 1) delta = -Math.round(Math.abs(delta) * m);
        }
        this.setAttribute(key, this.getAttribute(key) + delta);
    }

    getEnergy(): number {
        return this.player.resources.energy;
    }

    getMoney(): number {
        return this.player.resources.money;
    }

    addEnergy(delta: number): void {
        this.player.resources.energy = Utils.clamp(
            this.player.resources.energy + delta,
            GameConfig.ENERGY_MIN,
            GameConfig.ENERGY_MAX
        );
        EventBus.emit(GameEvents.RESOURCE_CHANGED, 'energy', this.player.resources.energy);
    }

    addMoney(delta: number): void {
        // 金钱允许为负（负数 = 欠债）：事件惩罚等支出可扣穿，免费行程不受影响，赚钱后自然回正
        this.player.resources.money += delta;
        if (delta > 0) {
            this.player.totalEarnedMoney += delta;
        }
        // 金钱首次达到带资进组解锁线：解锁标记（此后金钱回落也不回锁；富家千金开局走 setMoney 路径）
        this.unlockBuyinIfEligible();
        EventBus.emit(GameEvents.RESOURCE_CHANGED, 'money', this.player.resources.money);
    }

    /** 直接设置金钱（开局设定 / 富家千金天赋用；不走收入累计） */
    setMoney(value: number): void {
        this.player.resources.money = Math.max(0, value);
        this.unlockBuyinIfEligible();
        EventBus.emit(GameEvents.RESOURCE_CHANGED, 'money', this.player.resources.money);
    }

    /** 金钱首次达到 5 万解锁带资进组（已有标记则跳过；解锁时经纪人推一条通知） */
    unlockBuyinIfEligible(): void {
        if (this.hasFlag(GameConfig.BUYIN_UNLOCK_FLAG)) return;
        if (this.getMoney() >= GameConfig.BUYIN_UNLOCK_MONEY) {
            this.setFlag(GameConfig.BUYIN_UNLOCK_FLAG);
            // 台词表 #6（~豁免昵称）
            this.addManagerNotice('buyinUnlock',
                `「带资进组」解锁了，以后竞角失败的角色可以用投资换进组名额，投资为片酬×${GameConfig.BUYIN_MULTIPLIER}。`);
        }
    }

    getFame(): number {
        return this.player.fame;
    }

    /** 个体号召力系数（开局定档一次，终身 ⛔ 重摇——重摇会让粉丝数凭空跳变） */
    static rollFanCharisma(): number {
        const [min, max] = GameConfig.FANS_CHARISMA_RANGE;
        return Utils.randomFloat(min, max);
    }

    /**
     * 粉丝锚点 = fame × K × 个体号召力系数。
     * 全库唯一取锚点入口（月末收敛、脱粉扣减、开局咖位同步都读它），⛔ 别处再乘一次 FANS_ANCHOR_K。
     */
    getFanAnchor(): number {
        return Math.round(this.player.fame * GameConfig.FANS_ANCHOR_K * this.player.fanCharisma);
    }

    addFame(delta: number): void {
        const oldFame = this.player.fame;
        this.player.fame = Math.max(0, this.player.fame + delta);
        // 涨名气当场抬粉（即时项=实际涨的名气 × K × 系数 × 比例 × 抖动）——⛔ 滞后一个月才动，玩家体感是"发了帖没反应"
        const gained = this.player.fame - oldFame;
        if (gained > 0) {
            const instant = gained * GameConfig.FANS_ANCHOR_K * this.player.fanCharisma
                * GameConfig.FANS_IMMEDIATE_RATE
                * Utils.randomFloat(1 - GameConfig.FANS_IMMEDIATE_JITTER, 1 + GameConfig.FANS_IMMEDIATE_JITTER);
            this.player.fans += Math.round(instant);
        }
    }

    /** 设置名气触发咖位晋升检查 */
    setFame(value: number): void {
        this.player.fame = Math.max(0, value);
        // 咖位检查由 RankSystem 处理，这里只通知
        EventBus.emit('fameChanged', this.player.fame);
    }

    hasTag(tagId: string): boolean {
        // 测试桩/半份 state 可能没有 tags（正常档由迁移保证）：读取口一律当空表，别让判定链炸掉
        return (this.player.tags ?? []).includes(tagId);
    }

    /** 当前开局身份是否为 originId（身份不写进 tags，见 TagDef.pool） */
    hasOrigin(originId: string): boolean {
        return this.player.origin === originId;
    }

    /**
     * 设置开局身份（建角确认时调用一次；最多换一次，旧身份属性加成会先撤销）。
     * 身份不写进 tags、不占天赋槽；一次性效果（富家发钱等）走 TAG_EFFECTS，不可回滚。
     */
    setOrigin(originId: string): void {
        const def = GameData.TAG_MAP[originId];
        if (!def || def.pool !== 'origin') {
            console.warn(`[GameState] setOrigin: ${originId} 不是开局身份（pool='origin'）`);
            return;
        }
        if (this.player.origin === originId) return;
        const prev = this.player.origin ? GameData.TAG_MAP[this.player.origin] : undefined;
        if (prev?.attributeBonus) {
            (Object.keys(prev.attributeBonus) as AttributeKey[]).forEach(key => {
                this.addAttribute(key, -(prev.attributeBonus![key] ?? 0));
            });
        }
        this.player.origin = originId;
        if (def.attributeBonus) {
            (Object.keys(def.attributeBonus) as AttributeKey[]).forEach(key => {
                this.addAttribute(key, def.attributeBonus![key] ?? 0);
            });
        }
        TAG_EFFECTS[originId]?.(this);
        console.log(`[GameState] 开局身份: ${def.name}`);
    }

    addTag(tagId: string): void {
        if (!this.hasTag(tagId)) {
            if (!Array.isArray(this.player.tags)) this.player.tags = [];
            this.player.tags.push(tagId);
            // 应用标签属性加成（可能为空：加成形式不限于属性）
            const tag = GameData.TAG_MAP[tagId];
            if (tag?.attributeBonus) {
                const bonus = tag.attributeBonus;
                (Object.keys(bonus) as AttributeKey[]).forEach(key => {
                    this.addAttribute(key, bonus[key] ?? 0);
                });
                console.log(`[GameState] 获得标签: ${tag.name}`);
            }
            // 开局一次性口碑扣减（黑料缠身 TAG_401）：直接 `setAttribute`，⛔ 走 `addAttribute`——
            // 否则会被它自己的「负向口碑 ×1.5」再放大一次（−200 变 −300）。基值不是"损失事件"。
            if (tag?.startReputationPenalty) {
                this.setAttribute('reputation', this.getAttribute('reputation') + tag.startReputationPenalty);
                console.log(`[GameState] ${tag.name}：开局口碑 ${tag.startReputationPenalty}`);
            }
            // 「XX 减半」类负面特性（九漏鱼/五音不全/低情商）：**只砍训练获得的那部分**
            // （落点见 `GameState.trainingGain`）。
            // ⚠️ 2026-10-21 用户令：**不再砍开局底值**——挂上这条特性时初始属性原样不动，
            //    所以这里**不再有任何代码**（原来要记 `startAttrBase` 才能只砍随机那半，现已整条退役）。
            // 一次性特殊效果（机制型/事件型标签）：fire-once，removeTag 不回滚（语义不可逆）
            const effect = TAG_EFFECTS[tagId];
            if (effect) {
                effect(this);
            }
        }
    }

    /**
     * 训练获得某属性的**实际增量**：被「XX 减半」类负面特性砍半（九漏鱼/五音不全/低情商）。
     * ⛔ 只砍**正向获得**（训练扣属性的负向不砍）；一律向下取整。
     * ⛔ **只作用于训练**（2026-10-21 用户令）：开局属性、事件给的、杀青给的、商店买的都不受影响。
     * 落点＝`AttributeSystem.applyChanges`（全库唯一一处「训练加属性」的入口）。
     */
    trainingGain(key: AttributeKey, delta: number): number {
        if (delta > 0 && GameData.TAGS.some(t => t.pool === 'trait' && t.halvedAttr === key && this.hasTag(t.id))) {
            return Math.floor(delta / 2);
        }
        return delta;
    }

    // ===== 名气倍率类特性（「奖杯滚雪球」/「无效营销」）=====

    /**
     * 玩家身上某类「名气倍率」特性的乘积（没有则＝1；同类多张则相乘）。
     * ⛔ 只认 `pool:'trait'` 的开局特性（局内标签不参与）。
     */
    private traitMult(key: 'marketingFameMult' | 'reputationLossMult'): number {
        let m = 1;
        GameData.TAGS.forEach(t => {
            const v = t[key];
            if (t.pool === 'trait' && typeof v === 'number' && v > 0 && this.hasTag(t.id)) m *= v;
        });
        return m;
    }

    /**
     * **已得奖数（奖杯数）**＝`awardHistory` 里 `playerWon` 的条数
     * （与结算页「获奖陈列」那一排奖杯同一口径：一个奖种一条＝一座奖）。
     */
    awardCount(): number {
        return (this.player.awardHistory ?? []).filter(r => r.playerWon).length;
    }

    /**
     * 「奖越拿越多」类正面特性的**奖项名气倍率**：
     * `min(封顶, 1 + 已得奖数 × 步长)`。
     * **已得奖数含本次领奖**（领奖记录先入 `awardHistory` 再发钱 ⇒ 落点 `AwardSystem.payOut`）。
     * 没有这类特性＝1（倍率不动）。
     * @param winsUpTo 手动指定"截至第几条记录为止已得的奖数"——
     *   `payoutOfCeremony` 重算历史时**必须**传（那里不能直接读全量 `awardHistory`，
     *   否则会把后来的奖算进来，与实际发放对不上）。
     */
    awardFameMult(winsUpTo?: number): number {
        let step = 0;
        let cap = Infinity;
        GameData.TAGS.forEach(t => {
            if (t.pool !== 'trait' || !this.hasTag(t.id)) return;
            const s = t.awardFameStep;
            if (typeof s === 'number' && s > 0) {
                step = Math.max(step, s);
                cap = Math.min(cap, t.awardFameCap ?? Infinity);
            }
        });
        if (!step) return 1;
        const n = winsUpTo ?? this.awardCount();
        return Math.min(cap, 1 + n * step);
    }

    /**
     * **奖项发放**的那笔名气（乘「奖杯滚雪球」类正面特性的倍率）。
     * 落点＝`AwardSystem.payOut` 与 `AwardSystem.payoutOfCeremony`（两处必须同口径，
     * ⛔ 不许一处乘一处不乘——UI 读的就是 `payoutOfCeremony` 的数）。
     * ⛔ 只用于**获奖**那笔（提名不乘，见 `TagDef.awardFameStep` 注释）。
     */
    awardFameGain(base: number, winsUpTo?: number): number {
        return Math.round(base * this.awardFameMult(winsUpTo));
    }

    /**
     * **营销渠道**产出的名气 ×倍率（「无效营销」类负面特性）。
     * ⚠️ **营销渠道＝可扩展口径**（2026-10-21 用户令「后面会加别的营销渠道，不只是发微博」）：
     *    凡"靠经营/曝光换来的名气"都算营销渠道，**一律经本方法过一道**。
     *    现存渠道＝微博营业帖（整笔）／剧宣帖的「自营业」那半／公益帖（整笔）；
     *    **将来加新渠道（直播、代言、综艺曝光…）时，在新渠道的 fame 发放处调本方法即可**，
     *    ⛔ 别绕过它自己乘（否则「无效营销」对那条渠道失效）。
     * ⛔ 作品本身给的名气（杀青滴灌、剧宣帖的作品分红）、奖项、事件/行程**不走这里**。
     */
    marketingFameGain(base: number): number {
        return Math.round(base * this.traitMult('marketingFameMult'));
    }

    removeTag(tagId: string): void {
        const idx = (this.player.tags ?? []).indexOf(tagId);
        if (idx >= 0) {
            // 移除时也要撤销属性加成（可能为空）
            const tag = GameData.TAG_MAP[tagId];
            if (tag?.attributeBonus) {
                const bonus = tag.attributeBonus;
                (Object.keys(bonus) as AttributeKey[]).forEach(key => {
                    this.addAttribute(key, -(bonus[key] ?? 0));
                });
            }
            this.player.tags.splice(idx, 1);
        }
    }

    /** 设置/清除 flag（剧情进度标记用） */
    setFlag(flagId: string, value = true): void {
        this.player.flags[flagId] = value;
    }

    hasFlag(flagId: string): boolean {
        return !!this.player.flags[flagId];
    }

    /** 本季季号（特殊剧本附表 availableSeasons 的键；年=展示口径 1-indexed，与日历 trigger 同尺） */
    private seasonKey(year = this.player.year, month = this.player.month): string {
        return `Y${year + 1}Q${Math.floor((month - 1) / 3) + 1}`;
    }

    /** NPC 已接演（在途）的剧本 ID 集合：一部剧只拍一遍的那把尺，buildMarket 与 unlockScript 同读 */
    private npcShotIds(): Set<string> {
        return new Set(normalNpcSystem.getNpcs().flatMap(n => (n.activeJobs ?? []).map(j => j.scriptId)));
    }

    /**
     * 剧情解锁型特殊剧点灯（2026-10-14 茄子令「当月遇到他，剧本直接上货架，不用和别的剧本一样
     * 一季一季地上」）：置 unlockFlag + **当场**把这部剧挂进当前货架，不等季末换新。
     * 三条排除与 buildMarket 完全同尺：玩家已签过、NPC 已接演（一部剧只拍一遍）、本季不在附表窗口内
     * （窗口之外点了灯只能等下一次季末重建，而那时窗口已关＝这部剧这周目不拍，配置错由调用侧文案管）。
     * 插进去的位子在下次季末重建时自然复位（重建按 flag/窗口/npcShot 重算，不需要回滚）。
     */
    unlockScript(scriptId: string): void {
        const sp = GameData.SPECIAL_SCRIPTS.find(s => s.scriptId === scriptId);
        if (!sp) {
            // 附表查无＝这条 unlockScripts 配错了。旧口径在这里退化成 setFlag(scriptId)，照旧留痕但喊一声
            console.warn(`[剧情解锁] ${scriptId} 不在特殊剧本附表里，只按普通 flag 置位（剧挂不上架）`);
            this.setFlag(scriptId);
            return;
        }
        if (sp.unlockFlag) this.setFlag(sp.unlockFlag);
        if (!GameData.SCRIPT_MAP[scriptId]) return;
        if (!sp.availableSeasons.includes(this.seasonKey())) return;
        if (this.player.marketScripts.includes(scriptId)) return;
        if (this.player.contracts.some(c => c.scriptId === scriptId)) return;
        if (this.npcShotIds().has(scriptId)) return;
        this.player.marketScripts.push(scriptId);
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /** 推送一条经纪人通知（带资进组解锁/特殊天赋等事件用；历史入档页面 5b） */
    addManagerNotice(kind: import('../data/types').ManagerNotice['kind'], text: string): void {
        noticeSystem.push(kind, text);
    }

    /** 获取 NPC 好感度（未结识 = -1，结识后 0 起步） */
    getFavor(npcId: string): number {
        return this.player.relationships[npcId] ?? -1;
    }

    /** 结识 NPC：置 0 起步（仅当此前未结识） */
    meetNpc(npcId: string): void {
        if (this.getFavor(npcId) < 0) {
            // 自来熟（TAG_016）：新结识 NPC 初始好感 0→10
            this.player.relationships[npcId] = this.hasTag('TAG_016') ? 10 : 0;
        }
    }

    setFavor(npcId: string, value: number): void {
        this.player.relationships[npcId] = Utils.clamp(value, -1, 100);
    }

    addFavor(npcId: string, delta: number): void {
        this.setFavor(npcId, this.getFavor(npcId) + delta);
    }

    /** 背包持有数（缺省 0） */
    getItemCount(itemId: string): number {
        return this.player.items?.[itemId] ?? 0;
    }

    /** 入包（后续批次的朋友回赠/商店购买走这里） */
    addItems(itemId: string, count: number): void {
        const bag = this.player.items ?? (this.player.items = {});
        bag[itemId] = this.getItemCount(itemId) + count;
    }

    /** 出包（默认 1 件）：持有数不足则整体失败、⛔ 部分扣减——调用方据此决定要不要结算好感 */
    takeItem(itemId: string, count = 1): boolean {
        const left = this.getItemCount(itemId) - count;
        if (left < 0) return false;
        const bag = this.player.items ?? (this.player.items = {});
        if (left > 0) bag[itemId] = left;
        else delete bag[itemId];
        return true;
    }

    /** 剧本池刷新（滚动排期 2026-10-27：每月月末调一次，装「下个月决策日的在池货架」）
     *  servedYear/servedMonth：本池服务哪一年哪一月起；缺省 = 次月（月末换新装下月货架，在池含前 POOL_MONTHS-1 个月入池的剧） */
    refreshMarket(servedYear?: number, servedMonth?: number): void {
        const market = this.buildMarket(this.player.rank, servedYear, servedMonth);
        this.player.marketScripts = market;
        // 随机池模式：整剧本季度去重（本季度刷出的剧本本季度不再出现）；固定排期模式照排期固定上架，不去重
        if (this.player.marketMode !== 'scheduled') {
            this.player.scriptShown = market;
        }
        // 批6：在途工作跨换新保留（⛔ 不再清空 NPC 接角记录——拍摄/播出是跨季生命周期）。
        // 过气衰减（applyObsolescenceDecay）⛔ 跟着月度换新走：滚动换新改月度后衰减保持季节奏，
        // 由 doFinishMonth 在季末月单独调（否则衰减速度 ×3）。
        // （丙′ 2026-10-01）派角⛔ 在这里做——换新装的是**下季**货架，派它=NPC 又抢跑进玩家的选角季。
        // 收口派角由 doFinishMonth 在本方法之前调 sweepClosingScripts()（派刚收角的本季片单）。
        // 首季（新开局/读档兜底刷新）无需派角：第 1 季是选角季，制作从第 2 季开始。
        // 清除下架剧本的试镜资格保留
        this.player.auditionQualified = this.player.auditionQualified.filter(
            q => market.includes(q.scriptId)
        );
        // 竞角记忆（5c 结果）跟着资格一起修剪：下架的那位以后重新试镜要重掷（2026-10-10 落库整改）
        this.pruneAuditionReveal();
        EventBus.emit(GameEvents.SCRIPT_REFRESHED, market);
    }

    /**
     * 剧本池抽取算法（三级兜底，保证每次刷新池子非空）
     *
     * 多角色剧本（2026-09 定稿）：剧本不分等级，角色分等级（1-10，与咖位同构）。
     * 整剧进池：剧本**任一角色**等级 ∈ [rank-4, rank] 即抽中，整剧的可见角色一起开始试镜。
     * 池大小由 MARKET_SCRIPT_COUNT 控制（原 TIERS 席位倒金字塔已删除）。
     *
     * 约束：
     *  1. 已拍过 / 正在拍的剧本不再进入池子（排除 contracts 中 active+done 的 scriptId）
     *  2. 整剧本季度去重：scriptShown（本季度已刷出）不再进池
     *  3. 高等级角色（高于咖位可见档位）不显示，留待 NPC（sweep 时派给普通 NPC）
     *
     * 兜底链（上一级为空时降级到下一级，保证池子永不空）：
     *  ① 正常规则 → ② 放宽等级限制（仍排除已拍/已展示） → ③ 允许重拍（无视去重）
     */
    private buildMarket(rank: number, servedYear?: number, servedMonth?: number): string[] {
        // 固定排期模式：按排期表固定上架，无随机池、无兜底填充（咖位不够就只接低角色/上课/空周）
        if (this.player.marketMode === 'scheduled') {
            // 统一档期批修（2026-09-29）：排期分支与随机池同口径——已签/在拍/在播/已拍完的剧本
            // ⛔ 再上架（实测 Y3Q4 又能试镜在播《喜帖》的从犯：随机池有排除、排期分支没有）。
            // 滚动排期（2026-10-27）：货架 = 服务月起往前 POOL_MONTHS 个月内新入池的剧本并集
            // （在池货期 3 个月，池满次月开机）；排除后该月少一部是设计内（不换剧、不降级、不上架已拍）。
            const exclude = new Set(this.player.contracts.map(c => c.scriptId));
            const servedAbs = servedYear != null && servedMonth != null
                ? servedYear * 12 + servedMonth
                : this.player.year * 12 + this.player.month + 1;
            const base: string[] = [];
            for (let abs = servedAbs - POOL_MONTHS + 1; abs <= servedAbs; abs++) {
                const y = Math.floor((abs - 1) / 12);
                const m = abs - y * 12;
                for (const id of getScheduledScripts(y, m)) {
                    if (!exclude.has(id) && !base.includes(id)) base.push(id);
                }
            }
            // 特殊剧本（2026-10-06，docs/特殊剧本实装设计.md）：不进排期表，按附表 availableSeasons
            // 白名单自持上架；剧情解锁型还需 unlockFlag 已置位。窗口内逐月重复在架（拍过才被 exclude）。
            const y = Math.floor((servedAbs - 1) / 12);
            const m = servedAbs - y * 12;
            const season = this.seasonKey(y, m);
            // NPC 已接演的特殊剧不再上架（一部剧只拍一遍：NPC 版开拍后，玩家窗口内剩余季
            // 不再出现——错过即错过，多周目重试；普通剧靠排期不重复天然无此问题）
            const npcShot = this.npcShotIds();
            const specials = GameData.SPECIAL_SCRIPTS
                .filter(sp => sp.availableSeasons.includes(season))
                .filter(sp => !sp.unlockFlag || this.player.flags[sp.unlockFlag] === true)
                .map(sp => sp.scriptId)
                .filter(id => !exclude.has(id) && !npcShot.has(id) && GameData.SCRIPT_MAP[id] !== undefined);
            return [...base, ...specials];
        }

        const excludeScriptIds = new Set(
            // 批6：airing（播出中）同样排除——在播的剧本不再上架竞角
            this.player.contracts
                .filter(c => c.status === 'active' || c.status === 'airing' || c.status === 'done')
                .map(c => c.scriptId)
        );
        const shown = new Set(this.player.scriptShown);
        const visibleMin = Math.max(1, rank - 4);

        // ① 正常规则：剧本任一角色等级 ∈ [max(1, rank-4), rank]（排除已签 + 本季度已展示）
        let market = this.fillMarket(
            GameData.getScriptsByRoleLevelRange(visibleMin, rank)
                .filter(s => !excludeScriptIds.has(s.id) && !shown.has(s.id))
        );
        if (market.length > 0) return market;

        // ② 放宽等级：剧本任一角色等级 ≤ rank（仍排除已签 + 已展示）
        market = this.fillMarket(
            GameData.getScriptsByRoleLevelRange(1, rank)
                .filter(s => !excludeScriptIds.has(s.id) && !shown.has(s.id))
        );
        if (market.length > 0) return market;

        // ③ 允许重拍：无视去重（排除已签），保证非空
        return this.fillMarket(
            GameData.getScriptsByRoleLevelRange(1, rank)
                .filter(s => !excludeScriptIds.has(s.id))
        );
    }

    /** 随机抽满 MARKET_SCRIPT_COUNT 个剧本（池内候选不足时取全部；新手专属剧⛔ 进随机池） */
    private fillMarket(candidates: typeof GameData.SCRIPTS): string[] {
        const shuffled = Utils.deepClone(candidates.filter(s => s.id !== GameConfig.NEWBIE_SCRIPT_ID)).sort(() => Math.random() - 0.5);
        const count = Math.min(GameConfig.MARKET_SCRIPT_COUNT, shuffled.length);
        return shuffled.slice(0, count).map(s => s.id);
    }

    /**
     * 清扫收口季未签角色（丙′ 2026-10-01：派角时机改革——sweep 从「季初派下季片单」改为
     * 「换新夜派刚收角的本季片单」）。收口时玩家归属已定（5c 确认即终局）⇒ 未占用的位此刻才派给 NPC，
     * NPC 时钟结构不变（次月末首滴答）⇒ NPC 制作季 = 选角季 + 1，与玩家签的剧同季开工——
     * 「踢开工中的剧组」结构上不可能再发生（takeRoleFromNpc 保留为同夜 bindContractCast 绑定的回收）。
     * @param slate 收口池（= 刚结束选角季的片单）；缺省用当前货架（兼容旧调用）
     * @param absAt 派角那一晚的当月（收口夜乙段用：换月之后才派，开机月仍按收口夜那一晚推）
     */
    sweepUnclaimedRoles(slate?: string[], absAt?: number): void {
        for (const scriptId of slate ?? this.player.marketScripts) {
            const script = GameData.SCRIPT_MAP[scriptId];
            if (!script) continue;
            // 玩家签过/在演/已播完的角色位（⛔ 只认 active：airing/done 也归玩家 ——
            // 否则同一位在重拍季被再派一次，两条履历会让玩家的候选资格被顶掉）
            const ownedByPlayer = new Set(
                this.player.contracts.filter(c => c.scriptId === scriptId).map(c => c.roleIndex)
            );
            for (let i = 0; i < script.roles.length; i++) {
                // 玩家签约饰演的角色 → 跳过（不派给 NPC）
                if (ownedByPlayer.has(i)) continue;
                // （丙′）「合格→保留给玩家」分支已随「确认即作废」删除：收口时 auditionQualified
                // 已被 sweepClosingScripts 清空，不存在保留对象。
                // 派角本体（同位去重 + 角色锚点优先 + 随机兜底）统一在 NormalNpcSystem.fillRole，⛔ 两处各写一遍
                normalNpcSystem.fillRole(script, i, absAt);
            }
        }
    }

    /**
     * 修剪 5c 竞角记忆：只保留当前 auditionQualified 里还在的角色结果。
     * 资格下架或作废，该位记忆一起删，日后重新试镜重新掷（2026-10-10 竞角落库整改的配套；
     * 尺在 Utils.pruneAuditionReveal，ScriptSystem 换新那侧共用同一把）。
     */
    private pruneAuditionReveal(): void {
        Utils.pruneAuditionReveal(this.player);
    }

    /**
     * 收口夜派角（滚动排期 2026-10-27 改版，原 sweepClosingScripts）：doFinishMonth 在 refreshMarket
     * **之前**每月调用——普通剧收口夜 = 各自的池满月月末（入池月 + POOL_MONTHS − 1），一部剧一把钟；
     * 特殊剧仍按各自定角夜。
     * ⚠️ 清资格时序硬要求（v2.2b 外审）：「确认即作废」必须在派角之前落定——
     * 收口时所有 auditionQualified 都属收口季片单（每次换新已把资格修剪到当季市场），
     * 5c 关闭时未处理的已自动放弃，这里兜底清空，保证 sweep 时不存在任何「保留给玩家」的位
     * （否则该位整批无人饰演、候选池静默变薄）。
     * 唯一的留资格例外＝未到定角夜的特殊剧（2026-10-14 茄子定「七月试镜也是十二月底出结果」）：
     * 它的位同样留到定角夜那一季才派，两边同一条筛，不许一边留资格一边把位派出去。
     * 派角本身拆两段（2026-11-03 竞角重排批）：本法＝甲段（清资格＋算今夜剧单），
     * 乙段＝玩家表态之后的 dispatchClosingRoles；没有竞角队列时甲段当场派完，与旧口径同拍。
     */
    sweepClosingScripts(): void {
        const absNow = this.player.year * 12 + this.player.month;
        // 竞角结果与签约压到定角夜（2026-10-14 茄子定「七月试镜也是十二月底出结果」）：未到定角夜的
        // 特殊剧，资格与首掷记忆跨季留着（判据 revealDueNow，与 ScriptSystem.hasPendingReveals /
        // revealAuditions / resetForMarketRefresh 同一条尺），其余照旧清空
        this.player.auditionQualified = this.player.auditionQualified.filter(
            q => !castingNightDue(q.scriptId, absNow));   // 留＝定角夜还没到的那批（与 ScriptSystem 同一条尺）
        Utils.pruneAuditionReveal(this.player);   // 记忆跟着资格走（2026-10-10 落库整改那条不变）
        // 特殊剧收口派角（2026-10-13 茄子改判：「这个剧应该是触发特殊剧情之后才能上架」，覆盖 10-06
        // 那条「NPC 侧无视 unlockFlag」）：不许再按 availableSeasons 无条件入池。旧口径有两处实测后果
        // （探针 web/tests/_probe_901_takeover.mjs）：一是《颠倒姻缘》窗口首季 Y1Q2 的收口夜＝6 月末，
        // 而解锁它的咖啡厅偶遇最早 7 月才可能发生，NPC 永远抢在玩家能看见之前接走，flag 成死开关；
        // 二是窗口每覆盖一季就在该季收口夜再派一遍（上一轮 job 恰在同一夜的 advanceMonthJobs 里结清），
        // 于是一部剧三拍、履历 12 条、名气按 heat 发三遍。
        // 只在定角夜那一季交 NPC（2026-10-14 茄子令「玩家接或不接应该都是同一时间开拍的，以颠倒姻缘
        // 为例都是第二年一月开拍」）：与玩家签约同一夜定阵容，全组下一季开工；提前一季派就会出现
        // 「NPC 10 月开机、玩家明年 1 月开机」两条时间线。
        const pool = this.player.marketScripts.filter(id => {
            const deadline = specialDeadlineAbs(id);
            if (deadline != null) return deadline === absNow;   // 特殊剧：定角夜收口（原口径不动）
            const entry = poolEntryAbsOf(id);
            return entry != null && entry + POOL_MONTHS - 1 === absNow;   // 普通剧：池满夜收口
        });
        // 例外一条：玩家已签的特殊剧（因带合同被挤出货架、不在本季片单里）随时补齐其余位。
        // 终止条件（2026-10-18 治本）：只在这本剧还有没人演过的位时才进清单——done/airing 合同永久留着，
        // 这一路原本没有时点闸，于是玩家拍完那一季之后的**每个**换新夜都会把整组非玩家位再派一遍
        // （茄子抓虫：热度榜上反复出现主演全是 NPC 的《颠倒姻缘》）。判据用派角那同一把尺
        // `normalNpcSystem.hasUnplayedRole`，不许在这里另写一份「演过没有」。
        const specialIds = new Set(GameData.SPECIAL_SCRIPTS.map(sp => sp.scriptId));
        // 专属教学剧同款补位（2026-10-27 茄子令「新手剧和普通剧本一样可以派角给 NPC」）：
        // 玩家签了新手剧/晋升剧后，其余角色位照常派给 NPC（女主位由合同占用，sweep 自动跳过）
        const tutorialIds = new Set<string>([GameConfig.NEWBIE_SCRIPT_ID, GameConfig.PROMOTION_SCRIPT_ID]);
        const signedSpecial = [...new Set(this.player.contracts.map(c => c.scriptId))]
            .filter(id => !pool.includes(id) && (specialIds.has(id) || tutorialIds.has(id)) && GameData.SCRIPT_MAP[id] !== undefined)
            .filter(id => normalNpcSystem.hasUnplayedRole(id));
        // 存在闸型特殊剧（unlockFlag）的 NPC 补拍（2026-10-18 茄子令：「玩家没在咖啡厅解锁，也会有别的
        // NPC 演它并播出」）：玩家货架仍按 flag 门控（没解锁＝看不到、试不了），但 NPC 收口池在定角夜
        // 把这本剧也算上，免得整部剧因为玩家没去解锁而这一周目不存在。10-13「NPC 同受存在闸」的反对理由
        // （NPC 抢在解锁窗口之前接走）已随 10-14 定角夜挪到窗口末季而消失。只补「还没上架、没人拍过、
        // 玩家没签」的那几本；截止月＝定角夜，一季一判，不会重拍。
        const npcShot = this.npcShotIds();
        const unlockGated = GameData.SPECIAL_SCRIPTS
            .filter(sp => sp.unlockFlag)
            .filter(sp => specialDeadlineAbs(sp.scriptId) === absNow)
            .map(sp => sp.scriptId)
            .filter(id => GameData.SCRIPT_MAP[id] !== undefined)
            .filter(id => !this.player.marketScripts.includes(id))
            .filter(id => !npcShot.has(id))
            .filter(id => !this.player.contracts.some(c => c.scriptId === id));
        const slate = [...pool, ...signedSpecial, ...unlockGated];
        // 甲段只算不派（2026-11-03 竞角重排批）：这一晚有竞角队列＝玩家还没表态，位不能先派给 NPC
        // （收口夜先派给 NPC、玩家随后抢回同一个位＝「永无提名」那批 bug 的根：两边各演一遍，
        //  评选去重「同位只留最早播完一次」会把玩家那条静默丢掉）。
        // 剧单与当月存进月末结转包（跟着这次存盘走，读档后仍能派），乙段在玩家表态之后派。
        const handover = this.player.monthHandover;
        if (handover) {
            handover.closingSlate = slate;
            handover.closingAbs = absNow;
            return;
        }
        // 没有队列＝这一晚没人要表态，照旧当场派（与 2026-11-03 之前同拍）
        this.sweepUnclaimedRoles(slate);
    }

    /**
     * 收口夜乙段（玩家表态之后）：这一晚没被玩家签下的位派给 NPC。
     * 与甲段（sweepClosingScripts）分开是 2026-11-03 竞角重排批的硬顺序：**玩家先表态，再派剩下的角色**。
     * 剧单与当月从月末结转包里取（换过月了，不能按当月重算——货架已经换新）。
     */
    dispatchClosingRoles(): void {
        const handover = this.player.monthHandover;
        if (!handover) return;
        this.player.monthHandover = undefined;
        this.sweepUnclaimedRoles(handover.closingSlate, handover.closingAbs);
    }

    /**
     * 开月末结转包（竞角队列跨这次存盘）：队列行落进包，剧单与当月由甲段
     * （sweepClosingScripts）补。读档后队列原样再弹、成败不重掷。
     */
    openHandover(rows: { scriptId: string; roleIndex: number }[]): void {
        this.player.monthHandover = { rows, closingAbs: 0, closingSlate: [] };
    }

    /** 竞角队列摘一行（签/带资/放弃都走这里）：队列行与存档里的成败记忆同步收掉 */
    dropRevealRow(scriptId: string, roleIndex: number): void {
        const handover = this.player.monthHandover;
        if (!handover) return;
        handover.rows = handover.rows.filter(r => !(r.scriptId === scriptId && r.roleIndex === roleIndex));
    }

    /** 进入下一个月（年龄段/阶段检查用） */
    advanceMonth(): void {
        this.player.month++;
        if (this.player.month > 12) {
            this.player.month = 1;
            this.player.year++;
            this.player.age++;
        }
        this.player.week = 1;
        // 重置社交次数；营业状态改为按近3个月滚动判断，由 FanSystem 维护
        this.player.socialCounts = { chat: 0, invite: 0 };
        // 每到新月份自动清空行程安排（4周已执行完毕，需重新规划下月）
        this.player.schedule = [null, null, null, null];
        EventBus.emit(GameEvents.MONTH_END);
    }
}